<div class="container mx-auto px-0 py-0" *ngIf="!isEmbedded">
  <div class="bg-white shadow-md rounded-lg p-6" style="border-top: solid #6c757d 4px;">
    <div class="flex justify-between items-center mb-6">
      <div class="flex items-center space-x-2">
        <mat-icon>map</mat-icon>
        <h2 class="text-2xl font-bold mb-0">Pläne</h2>
      </div>
      <div class="flex space-x-2">
        <button (click)="drawPlan()" mat-stroked-button color="primary">
          <mat-icon>draw</mat-icon>
          Plan zeichnen
        </button>
        <button (click)="createPlan()" mat-raised-button color="primary">Plan erstellen</button>
      </div>
    </div>

    <!-- Search and Filter -->
    <div class="mb-6">
      <div class="flex flex-col md:flex-row gap-4">
        <div class="flex-1">
          <div class="w-full max-w-sm min-w-[200px]">
            <div class="relative">
              <input
                class="w-full bg-transparent placeholder:text-slate-400 text-slate-700 text-sm border border-slate-200 rounded-md pl-3 pr-28 py-2 transition duration-300 ease focus:outline-none focus:border-slate-400 hover:border-slate-300 shadow-sm focus:shadow"
                placeholder="Search plans..." 
                [(ngModel)]="searchValue"
                (input)="onSearch($event)"
              />
              <button
                class="absolute top-1 right-1 flex items-center rounded bg-slate-800 py-1 px-2.5 border border-transparent text-center text-sm text-white transition-all shadow-sm hover:shadow focus:bg-slate-700 focus:shadow-none active:bg-slate-700 hover:bg-slate-700 active:shadow-none disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
                type="button"
                *ngIf="searchValue"
                (click)="searchValue = ''; onSearch($event)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 mr-2">
                  <path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd" />
                </svg>
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div *ngIf="isLoadingResults" class="flex justify-center items-center py-8">
      <mat-progress-spinner color="primary" mode="indeterminate"></mat-progress-spinner>
    </div>

    <!-- Plan Table -->
    <div class="mt-8 flow-root" *ngIf="!isLoadingResults">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table class="relative min-w-full divide-y divide-gray-300 dark:divide-white/15">
            <thead>
              <tr>
                <th scope="col" class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white" style="width: 6%;">Bezeichnung</th>
                <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Sektion</th>
                <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Aktiv</th>
                <th scope="col" class="whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-0">
                  <span class="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-900">
              <tr 
                *ngFor="let plan of dataSource.data; let i = index" 
                (click)="onPlanClick(plan)" 
                class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
                [ngClass]="{'bg-gray-50 dark:bg-gray-800': i % 2 === 1}">
                <td class="whitespace-nowrap py-1 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 dark:text-white" style="width: 6%;">
                  <div class="flex flex-col">
                    <span class="font-medium">{{ plan.bezeichnung }}</span>
                    <span class="text-sm text-gray-500 italic" *ngIf="plan.anmerkungen">{{ plan.anmerkungen }}</span>
                  </div>
                </td>
                <td class="whitespace-nowrap px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <span *ngIf="plan.sektion">{{ plan.sektion.name }}</span>
                  <span *ngIf="!plan.sektion" class="text-gray-400">Unbekannt</span>
                </td>
                <td class="whitespace-nowrap px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                        [ngClass]="getStatusClasses(plan.active)">
                    {{ plan.active ? 'Aktiv' : 'Inaktiv' }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                  <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                        [ngClass]="getActivationStatusClasses(plan.is_active)">
                    <mat-icon class="text-sm mr-1">{{ plan.is_active ? 'check_circle' : 'radio_button_unchecked' }}</mat-icon>
                    {{ plan.is_active ? 'Aktiv' : 'Inaktiv' }}
                  </span>
                </td>
                <td class="whitespace-nowrap py-1 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                  <div class="flex space-x-2">
                    <button
                      *ngIf="!plan.is_active"
                      (click)="activatePlan(plan); $event.stopPropagation()"
                      mat-button
                      color="primary"
                      class="slim-button">
                      <mat-icon class="text-sm mr-1">play_arrow</mat-icon>
                      Aktivieren
                    </button>
                    <button
                      *ngIf="plan.is_active"
                      (click)="deactivatePlan(plan); $event.stopPropagation()"
                      mat-button
                      color="warn"
                      class="slim-button">
                      <mat-icon class="text-sm mr-1">pause</mat-icon>
                      Deaktivieren
                    </button>
                    <button
                      (click)="drawEditPlan(plan); $event.stopPropagation()"
                      mat-button
                      color="primary"
                      class="slim-button">
                      <mat-icon class="text-sm mr-1">draw</mat-icon>
                      Zeichnen
                    </button>
                    <button
                      (click)="onPlanClick(plan); $event.stopPropagation()"
                      mat-button
                      color="primary"
                      class="slim-button">
                      Bearbeiten
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- No Data Message -->
    <div class="text-center py-8 text-gray-500" *ngIf="dataSource.data.length === 0 && !isLoadingResults">
      <mat-icon class="text-4xl mb-2">folder_open</mat-icon>
      <p>Keine Pläne gefunden</p>
    </div>

    <!-- Paginator -->
    <mat-paginator
      *ngIf="!isLoadingResults && totalItems > 0"
      [length]="totalItems"
      [pageSizeOptions]="[10, 25, 50]"
      (page)="onPageChange($event)"
      showFirstLastButtons
      aria-label="Select page of plans">
    </mat-paginator>
  </div>
</div>

<!-- Embedded Mode -->
<div class="plan-container" [class.embedded]="isEmbedded" *ngIf="isEmbedded">
  <!-- Loading Spinner -->
  <div class="flex justify-center items-center py-8" *ngIf="isLoadingResults">
    <mat-progress-spinner color="primary" mode="indeterminate"></mat-progress-spinner>
  </div>

  <!-- Plan Table -->
  <div class="mt-8 flow-root" *ngIf="!isLoadingResults">
    <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
      <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
        <table class="relative min-w-full divide-y divide-gray-300 dark:divide-white/15">
          <thead>
            <tr>
              <th scope="col" class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0 dark:text-white" style="width: 6%;">Bezeichnung</th>
              <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Sektion</th>
              <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
              <th scope="col" class="whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-0">
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-white/10 dark:bg-gray-900">
            <tr 
              *ngFor="let plan of dataSource.data; let i = index" 
              (click)="onPlanClick(plan)" 
              class="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
              [ngClass]="{'bg-gray-50 dark:bg-gray-800': i % 2 === 1}">
              <td class="whitespace-nowrap py-1 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 dark:text-white" style="width: 6%;">
                <div class="flex flex-col">
                  <span class="font-medium">{{ plan.bezeichnung }}</span>
                  <span class="text-sm text-gray-500 italic" *ngIf="plan.anmerkungen">{{ plan.anmerkungen }}</span>
                </div>
              </td>
              <td class="whitespace-nowrap px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                <span *ngIf="plan.sektion">{{ plan.sektion.name }}</span>
                <span *ngIf="!plan.sektion" class="text-gray-400">Unbekannt</span>
              </td>
              <td class="whitespace-nowrap px-2 py-2 text-sm text-gray-500 dark:text-gray-400">
                <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                      [ngClass]="getStatusClasses(plan.active)">
                  {{ plan.active ? 'Aktiv' : 'Inaktiv' }}
                </span>
              </td>
              <td class="whitespace-nowrap py-1 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                <button
                  (click)="onPlanClick(plan); $event.stopPropagation()"
                  mat-button
                  color="primary"
                  class="slim-button">
                  Bearbeiten
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- No Data Message -->
  <div class="text-center py-8 text-gray-500" *ngIf="dataSource.data.length === 0 && !isLoadingResults">
    <mat-icon class="text-4xl mb-2">folder_open</mat-icon>
    <p>Keine Pläne gefunden</p>
  </div>
</div>
